/**
 * Spec 084 review F2: single source for `system:warningFlags` values, instead of four separate
 * copies (DocumentTable, Inbox, DocumentDetail, UploadDocumentDetail) that had drifted apart
 * (two different-width sets).
 */

/** Full i18n mapping — used by the two banners that render a human-readable sentence per flag. */
export const WARNING_FLAG_I18N_KEYS: Record<string, string> = {
  ai_classification_failed: 'components.document_detail.warning_ai_classification_failed',
  ai_response_unparsable: 'components.document_detail.warning_ai_response_unparsable',
  ai_extraction_failed: 'components.document_detail.warning_ai_extraction_failed',
  prompt_injection_suspected: 'components.document_detail.warning_prompt_injection_suspected',
};

/**
 * Narrower subset: "the classification or extraction is a guess", shown as the list-row warning
 * triangle (DocumentTable, Inbox). `prompt_injection_suspected` is deliberately excluded here —
 * it flags a security concern about the document's content, not an unreliable AI result, and
 * blending it into the same guessed-category triangle would misrepresent what the triangle means.
 * It still shows in the two banners via `WARNING_FLAG_I18N_KEYS` above.
 */
export const AI_GUESS_WARNING_FLAGS = [
  'ai_classification_failed',
  'ai_response_unparsable',
  'ai_extraction_failed',
];

export function parseWarningFlags(raw: string | undefined | null): string[] {
  return (raw ?? '')
    .split(',')
    .map((flag) => flag.trim())
    .filter((flag) => flag.length > 0);
}

/** Flags recognized by `WARNING_FLAG_I18N_KEYS`, in encounter order — for banner rendering. */
export function recognizedWarningFlags(raw: string | undefined | null): string[] {
  return parseWarningFlags(raw).filter((flag) => flag in WARNING_FLAG_I18N_KEYS);
}

/** True if any of `AI_GUESS_WARNING_FLAGS` is present — for the list-row warning triangle. */
export function hasAiGuessWarning(raw: string | undefined | null): boolean {
  const flags = parseWarningFlags(raw);
  return AI_GUESS_WARNING_FLAGS.some((flag) => flags.includes(flag));
}

/**
 * Spec 100 E7: flags accumulate across runs, and two of them share one sentence. Keep the first
 * flag per resolved sentence — deduplicating on the text, not the flag name or i18n key.
 */
export function distinctSentenceFlags(
  flags: string[],
  translate: (key: string) => string,
): string[] {
  const seen = new Set<string>();
  return flags.filter((flag) => {
    const sentence = translate(WARNING_FLAG_I18N_KEYS[flag]);
    if (seen.has(sentence)) return false;
    seen.add(sentence);
    return true;
  });
}
